import { useMemo } from 'react'

import {
  useFilters, useModals, useNumericParams, useSetState,
} from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import type { AttachmentModel } from '@models/types'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { ShowCreateOrDuplicateTrainingModule } from '@hooks/useTrainingModule'

import CreateOrDuplicateTrainingCourseModal from '@modals/CreateOrDuplicateTrainingCourseModal'
import CreateOrDuplicateTrainingModuleModal from '@modals/CreateOrDuplicateTrainingModuleModal'
import CreateOrEditAttachmentModal from '@modals/CreateOrEditAttachmentModal'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrDuplicateTrainingCourseModal: false,
  showCreateOrDuplicateTrainingModuleModal: false,
  showCreateOrEditAttachmentModal: false,
  showCreateTrainingModuleModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditAttachmentModal: {
      closeModal: () => setState({ showCreateOrEditAttachmentModal: false }),
      deleteAttachment: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateAttachment: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrDuplicateTrainingCourseModal: {
      closeModal: () => setState({ showCreateOrDuplicateTrainingCourseModal: false }),
      createTrainingCourse: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      duplicateTrainingCourse: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrDuplicateTrainingModuleModal: {
      closeModal: () => setState({ showCreateOrDuplicateTrainingModuleModal: false }),
      createTrainingModule: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      duplicateTrainingModule: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }
  return componentCallbacks[component] || {}
}

const Details = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrDuplicateTrainingCourseModal,
    showCreateOrDuplicateTrainingModuleModal,
    showCreateOrEditAttachmentModal,
  } = state

  const { trainingCourseId } = useNumericParams()

  const {
    callbacks: filterCallbacks,
    defaultFilters,
  } = useFilters()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      deleteTrainingModule: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      showCreateOrDuplicateTrainingModuleModal: (payload: ShowCreateOrDuplicateTrainingModule) => {
        setModalData('CreateOrDuplicateTrainingModuleModal', payload)
        setState({ showCreateOrDuplicateTrainingModuleModal: true })
      },
      showCreateOrDuplicateTrainingCourseModal: (payload: object) => {
        setModalData('CreateOrDuplicateTrainingCourseModal', payload)
        setState({ showCreateOrDuplicateTrainingCourseModal: true })
      },
      showCreateOrEditAttachmentModal: (payload: { attachment: AttachmentModel }) => {
        setModalData('CreateOrEditAttachmentModal', payload)
        setState({ showCreateOrEditAttachmentModal: true })
      },
      updateTrainingCourse: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    defaultFilters,
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent trainingCourseId={trainingCourseId} />

        <CreateOrDuplicateTrainingCourseModal
          callbacks={callbacks('CreateOrDuplicateTrainingCourseModal', setState)}
          showModal={showCreateOrDuplicateTrainingCourseModal}
        />

        <CreateOrEditAttachmentModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditAttachmentModal', setState)}
          showModal={showCreateOrEditAttachmentModal}
        />

        <CreateOrDuplicateTrainingModuleModal
          callbacks={callbacks('CreateOrDuplicateTrainingModuleModal', setState)}
          showModal={showCreateOrDuplicateTrainingModuleModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
